import { Box, BoxProps } from '@chakra-ui/react';

import { usePresenceStatus } from '#features/presence/hooks/usePresence';
import { PresenceStatuses, type PresenceStatus } from '#features/presence/model/presenceStore';

enum Sizes {
  xs2 = '2xs',
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  md2 = '2md',
  lg = 'lg',
  lg2 = '2lg',
  xl = 'xl',
  xxl = 'xxl',
}

/**
 * Размер точки — доля от аватара, а не отдельное число.
 *
 * Долей, а не пикселями, по двум причинам. Первая: аватар бывает адаптивным — строка человека
 * просит `{ base: "sm", md: "md" }`, и таблица по ключу-объекту молча отдавала `undefined`.
 * Точка схлопывалась до внутреннего отступа и превращалась в крапинку; заметно это стало не
 * сразу, потому что в соседних списках размер приходил строкой и всё выглядело правильно.
 * Вторая: доля считается прямо в вёрстке (`cqw` от аватара-контейнера), поэтому на любом
 * брейкпоинте точка попадает в размер сама, без второй таблицы значений.
 *
 * В строках и рядах доля — четверть: там точка единственный носитель «в сети», рядом с ней об
 * этом ничего не написано. У портретов доля меньше и оставлена прежней: под портретом идёт
 * строка присутствия словами, и раздутая точка спорила бы с ней за один смысл.
 */
export const PRESENCE_DOT_RATIO = {
  [Sizes.xs2]: 0.25,
  [Sizes.xs]: 0.25,
  [Sizes.sm]: 0.25,
  [Sizes.md]: 0.25,
  [Sizes.md2]: 0.17,
  [Sizes.lg]: 0.14,
  [Sizes.lg2]: 0.12,
  [Sizes.xl]: 0.09,
  [Sizes.xxl]: 0.08,
};

/** Доля в единицах контейнера: аватар объявлен `containerType: inline-size`. */
export const dotSizeOf = (size: `${Sizes}`) => `${(PRESENCE_DOT_RATIO[size] ?? 0.25) * 100}cqw`;

const colors: Record<PresenceStatus, string> = {
  [PresenceStatuses.OFFLINE]: 'transparent',
  [PresenceStatuses.ONLINE]: 'status.live.solid',
  [PresenceStatuses.AWAY]: 'yellow.400',
};

type OnlinePresenceProps = {
  userId: string;
  /**
   * Готовый статус — для экранов, которые и так держат его в руках (списки на
   * `playerAvailability`). Передан — точка рисуется по нему, и подписки на общий опрос не
   * возникает: иначе тот же самый статус приезжал бы на экран дважды, двумя запросами с разным
   * временем старта, и точка расходилась бы с подписью под ней на те же полминуты, ради
   * устранения которых присутствие когда-то и свели в один запрос.
   *
   * Владелец данных обязан передавать проп всегда, в том числе `null` на время загрузки:
   * отсутствие пропа означает «статуса у меня нет, спроси сам», и `undefined` от неготовых данных
   * включал бы подписку на один рендер, чтобы тут же её снять.
   */
  status?: PresenceStatus | null;
  size?: `${Sizes}`;
} & BoxProps;

/**
 * Статус, при котором точку видно. Вынесен из компонента, потому что решение нужно ДО отрисовки:
 * аватар вырезает под точку отверстие в себе, и вырезать его у офлайнового значило бы оставить в
 * лице дырку без причины.
 */
export const useVisiblePresence = (
  userId?: string | null,
  providedStatus?: PresenceStatus | null
) => {
  // Пустой userId у хука — это «не подписывайся»: watch/unwatch не вызывается, статус не читается.
  const watchedStatus = usePresenceStatus(
    providedStatus === undefined ? (userId ?? undefined) : undefined
  );
  const status = providedStatus === undefined ? watchedStatus : providedStatus;

  // undefined — «ещё не спросили», и это не то же самое, что «не в сети»: рисовать нечего.
  return !status || status === PresenceStatuses.OFFLINE ? null : status;
};

/**
 * Точка присутствия. Спрашивает статус у общего опроса — раньше он вычислялся здесь же из
 * «времени последнего визита», то есть из времени последнего запроса человека к серверу: фоновый
 * рефетч держал точку зелёной у отошедшего, а читающий страницу без кликов, наоборот, гас.
 * Вдобавок расчёт жил в useMemo и со временем не пересчитывался — открытая вкладка замораживала
 * индикатор на том, что было при загрузке.
 */
export const OnlinePresence = ({
  userId,
  status: providedStatus,
  size = Sizes.md,
  ...props
}: OnlinePresenceProps) => {
  const status = useVisiblePresence(userId, providedStatus);

  if (!status) {
    return null;
  }

  /*
    Кольца вокруг точки больше нет. Оно было сплошной заливкой, которую каждый экран обязан был
    вручную подогнать под фон под аватаром (`--status-ring-color` в десяти местах), и подогнать
    правильно удавалось не везде: у списка рекомендованных под аватаром полупрозрачная подложка,
    и совпадающего с ней сплошного цвета не существует вовсе, а список чатов на телефоне теряет
    фон целиком. Отделение точки от лица теперь делает вырез в самом аватаре — сквозь него видно
    настоящую поверхность, какой бы она ни была.
  */
  return (
    <Box
      // Признак для проверок и отладки: точку иначе не отличить от прочих кружков в аватаре —
      // после того как заливка переехала внутрь корня, «первый div» стал маской, а не точкой,
      // и проверка «точки нет у офлайнового» проходила на любой разметке.
      data-presence={status}
      padding="0.1em"
      boxSize={dotSizeOf(size)}
      bgColor={colors[status]}
      borderRadius="full"
      {...props}
    />
  );
};
